import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord } from '@warp-drive/utilities/json-api';
import type EmployeeService from 'tio-employee/services/employee';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store } from '@warp-drive/core';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type Transition from '@ember/routing/transition';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { ContributionActionItem } from 'tio-employee/services/reimagined-data';
import type { ReimaginedHomeModel } from 'tio-employee/routes/authenticated/reimagined/home';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type DashboardRouteModel = Resolved<ReturnType<AuthenticatedDashboardRoute['model']>>;

/**
 * Everything the beta home page (`components/beta/home`) renders from.
 *
 * `reimagined` is the shared snapshot bundle; the two extra fields cover
 * figures the 2026 design asks for that the bundle does not carry.
 */
export interface BetaHomeRouteModel {
  reimagined: ReimaginedHomeModel;
  /** Server-authored loan-repayment action items for "Needs Your Attention". */
  actionItems: ContributionActionItem[];
  /** Tuition assistance used this benefit year; null when unresolvable. */
  tuitionYearToDate: number | null;
}

export default class AuthenticatedDashboardRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare employee: EmployeeService;
  @service declare partner: PartnerService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service('beta-experience') declare betaExperience: BetaExperienceService;
  @service declare reimaginedData: ReimaginedDataService;

  // check for mentorship form submission re-route without passing
  // as a param that needs to be reset to prevent persistence
  beforeModel(transition: Transition) {
    if (transition.to?.queryParams.formSubmitted) {
      const successMessage = this.intl.t('tuition_assistance.mentoring.submit_success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    }
  }

  async model() {
    // Refetch the SLR widget's server-driven state on each dashboard entry —
    // its trackedFunction inputs never change after login, so without this a
    // resolved (or newly arisen) alert would keep the tile's stale state for
    // the whole SPA session. All three tile inputs, not just alerts: the
    // partially-applied and loan-standing reads go stale the same way and feed
    // the same `hasError`.
    this.employee.refreshSlrState();

    const personId = this.sessionContext.user.person.id!;

    // Only load accounts.loans - this is what loan-summary widget needs
    // Other employee/company data is already loaded via /me endpoint in session-context
    const { content: personContent } = (await this.store.request(
      findRecord<PersonModel>('person', personId, {
        include: 'accounts.loans,accounts.postal-address',
        reload: true,
      })
    )) as { content: { data: PersonModel } };
    const person = personContent.data;

    // isTaOnly uses employees.plan which is already loaded via /me endpoint
    const employees = this.sessionContext.user.employees;
    const isTaOnlyCompany = this.partner.isTaOnly;
    const companyKey = employees.map((e) => e.plan?.companyKey);
    const taOnlyPlanId = this.partner.companySettings?.tuitionAssistance?.taOnlyPlanId;
    const isTaOnlyEmployee = taOnlyPlanId ? companyKey.includes(taOnlyPlanId) : false;

    // Get wellness data from parent authenticated route
    const authenticatedModel = this.modelFor('authenticated') as {
      wellnessData: Record<string, unknown> | null;
    };

    // When beta is enabled, fetch beta home data alongside legacy data
    if (this.betaExperience.isEnabled) {
      const [
        financialSnapshot,
        dataSourceInfo,
        employerContributions,
        pslfData,
        tasData,
        section127Info,
        // The last two feed figures the snapshot bundle does not carry. Neither is
        // load-bearing for the page, so a failure must degrade rather than blank
        // the dashboard — see how each is unwrapped below.
        contributionHistory,
        tasUsedAmount,
      ] = await Promise.all([
        this.reimaginedData.fetchFinancialSnapshot(),
        this.reimaginedData.getDataSourceInfo(),
        this.reimaginedData.fetchEmployerContributions(),
        this.reimaginedData.fetchPslfData(),
        this.reimaginedData.fetchTasData(),
        this.reimaginedData.fetchSection127Info(),
        this.reimaginedData.fetchContributionHistory().catch((error: unknown) => {
          console.warn('[dashboard] beta: contribution history unavailable', error);
          return null;
        }),
        this.reimaginedData.fetchTasUsedAmount().catch((error: unknown) => {
          console.warn('[dashboard] beta: TA year-to-date unavailable', error);
          return null;
        }),
      ]);

      const reimagined: ReimaginedHomeModel = {
        financialSnapshot,
        dataSourceInfo,
        employerContributions,
        pslfData,
        tasData,
        section127Info,
      };

      /**
       * `fetchTasUsedAmount` swallows its own errors and resolves
       * `{ yearToDate: 0, isLiveData: false }`, so the `.catch()` above can never
       * turn a failed request into null on its own — a TAS/OpenSearch outage would
       * otherwise render a confident `$0` for an unknown amount.
       *
       * `isLiveData: false` also covers demo mode and a missing employee id, so
       * only the non-demo case becomes null: in a demo the fixture value IS the
       * intended number.
       */
      const tuitionYearToDate =
        tasUsedAmount && (tasUsedAmount.isLiveData || this.reimaginedData.isDemoMode)
          ? tasUsedAmount.yearToDate
          : null;

      const beta: BetaHomeRouteModel = {
        reimagined,
        actionItems: contributionHistory?.actionItems ?? [],
        tuitionYearToDate,
      };

      return {
        person,
        isTaOnly: isTaOnlyCompany || isTaOnlyEmployee,
        wellnessData: authenticatedModel.wellnessData,
        beta,
      };
    }

    return {
      person,
      isTaOnly: isTaOnlyCompany || isTaOnlyEmployee,
      wellnessData: authenticatedModel.wellnessData,
    };
  }
}
